import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../api/api';

const DataContext = createContext();

export const useData = () => {
    const context = useContext(DataContext);
    if (!context) {
        throw new Error('useData must be used within a DataProvider');
    }
    return context;
};

export const DataProvider = ({ children }) => {
    const [voitures, setVoitures] = useState([]);
    const [clients, setClients] = useState([]);
    const [loadingVoitures, setLoadingVoitures] = useState(false);
    const [loadingClients, setLoadingClients] = useState(false);
    const [lastFetchVoitures, setLastFetchVoitures] = useState(0);
    const [lastFetchClients, setLastFetchClients] = useState(0);

    const CACHE_TIME = 1000 * 60 * 5; // 5 minutes cache

    const fetchVoitures = useCallback(async (force = false) => {
        const now = Date.now();
        if (!force && voitures.length > 0 && (now - lastFetchVoitures < CACHE_TIME)) {
            return voitures;
        }

        setLoadingVoitures(true);
        try {
            const res = await api.get('/voitures');
            setVoitures(res.data);
            setLastFetchVoitures(now);
            return res.data;
        } catch (err) {
            console.error('Error fetching voitures:', err);
            throw err;
        } finally {
            setLoadingVoitures(false);
        }
    }, [voitures, lastFetchVoitures]);

    const fetchClients = useCallback(async (force = false) => {
        const now = Date.now();
        if (!force && clients.length > 0 && (now - lastFetchClients < CACHE_TIME)) {
            return clients;
        }

        setLoadingClients(true);
        try {
            const res = await api.get('/clients');
            setClients(res.data);
            setLastFetchClients(now);
            return res.data;
        } catch (err) {
            console.error('Error fetching clients:', err);
            throw err;
        } finally {
            setLoadingClients(false);
        }
    }, [clients, lastFetchClients]);

    const findVoitureById = useCallback((id) => {
        return voitures.find(v => v.id === parseInt(id));
    }, [voitures]);

    const findClientById = useCallback((id) => {
        return clients.find(c => c.id === parseInt(id));
    }, [clients]);

    const value = {
        voitures,
        clients,
        loadingVoitures,
        loadingClients,
        fetchVoitures,
        fetchClients,
        findVoitureById,
        findClientById
    };

    return (
        <DataContext.Provider value={value}>
            {children}
        </DataContext.Provider>
    );
};
